import isNumber from 'lodash/isNumber';

interface Props {
  styles: Record<string, unknown>;
  props: Record<string, unknown>;
}

const getRelativeSizes = ({
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
}: Record<string, unknown>) => ({
  relativeWidth: (width as string)?.includes('%'),
  relativeHeight: (height as string)?.includes('%'),
  relativeMinWidth: (minWidth as string)?.includes('%'),
  relativeMinHeight: (minHeight as string)?.includes('%'),
  relativeMaxWidth: (maxWidth as string)?.includes('%'),
  relativeMaxHeight: (maxHeight as string)?.includes('%'),
});

/**
 * Calculates the modal's size in css format
 *
 * @param object
 * @param {Record<string, unknown>} styles css properties applied to modal from the editor toolbox (with calculated size values)
 * @param {Record<string, unknown>} props the custom sizing properties applied to modal from the toolbox
 * @returns {Record<string, unknown>} - the calculated css sizes to apply to the modal in the editor, which includes the inset padding
 * */
export const useModalSizing = ({ styles, props }: Props) => {
  // get toolbox custom values for sizing, default to original if not set by user
  // (if there is no custom sizing it means it has never been edited, so modalStyles will not be a css string)
  const { width, height, minWidth, minHeight, maxWidth, maxHeight } = props;

  const {
    relativeWidth,
    relativeHeight,
    relativeMinWidth,
    relativeMinHeight,
    relativeMaxWidth,
    relativeMaxHeight,
  } = getRelativeSizes({ width, height, minWidth, minHeight, maxWidth, maxHeight });

  const marginLeft = isNumber(styles.marginLeft)
    ? `${styles.marginLeft}px`
    : styles.marginLeft || '0px';
  const marginTop = isNumber(styles.marginTop)
    ? `${styles.marginTop}px`
    : styles.marginTop || '0px';
  const marginRight = isNumber(styles.marginRight)
    ? `${styles.marginRight}px`
    : styles.marginRight || '0px';
  const marginBottom = isNumber(styles.marginBottom)
    ? `${styles.marginBottom}px`
    : styles.marginBottom || '0px';

  const cssWidth = relativeWidth ? `${width} - ${marginLeft} - ${marginRight}` : width;
  const cssHeight = relativeHeight ? `${height} - ${marginTop} - ${marginBottom}` : height;
  const cssMinWidth = relativeMinWidth ? `${minWidth} - ${marginLeft} - ${marginRight}` : minWidth;
  const cssMinHeight = relativeMinHeight
    ? `${minHeight} - ${marginTop} - ${marginBottom}`
    : minHeight;
  const cssMaxWidth = relativeMaxWidth ? `${maxWidth} - ${marginLeft} - ${marginRight}` : maxWidth;
  const cssMaxHeight = relativeMaxHeight
    ? `${maxHeight} - ${marginTop} - ${marginBottom}`
    : maxHeight;

  return {
    sizes: {
      width: relativeWidth ? `calc(${cssWidth})` : width,
      height: relativeHeight ? `calc(${cssHeight})` : height,
      minWidth: relativeMinWidth ? `calc(${cssMinWidth})` : minWidth,
      minHeight: relativeMinHeight ? `calc(${cssMinHeight})` : minHeight,
      maxWidth: relativeMaxWidth ? `calc(${cssMaxWidth})` : maxWidth,
      maxHeight: relativeMaxHeight ? `calc(${cssMaxHeight})` : maxHeight,
    } as Record<string, string | undefined>,
  };
};
